import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const SVG_WIDTH = 800;
const SVG_HEIGHT = 400;
const MARGIN = { top: 20, right: 30, bottom: 40, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: { fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '4px' },
  subtitle: { fontSize: '12px', color: '#9ca3af', marginBottom: '20px' },
  tabRow: {
    display: 'flex',
    gap: '0px',
    marginBottom: '16px',
    borderBottom: '1px solid #e5e7eb',
  },
  tab: {
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    transition: 'color 0.15s, border-color 0.15s',
  },
  tabActive: {
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#2563eb',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid #2563eb',
    cursor: 'pointer',
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  selectLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
  },
  select: {
    padding: '6px 10px',
    fontSize: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    color: '#1f2937',
    outline: 'none',
    cursor: 'pointer',
  },
  infoCardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '10px',
    marginBottom: '16px',
  },
  infoCard: {
    background: '#f9fafb',
    borderRadius: '6px',
    padding: '10px 12px',
    textAlign: 'center',
  },
  infoValue: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#1f2937',
  },
  infoLabel: {
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '2px',
  },
  infoSublabel: {
    fontSize: '10px',
    color: '#9ca3af',
    marginTop: '1px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function getBarColor(halfLife) {
  if (halfLife <= 3) return '#16a34a';
  if (halfLife <= 6) return '#f59e0b';
  return '#dc2626';
}

export default function PriceHalfLifeChart({ data }) {
  const decaySvgRef = useRef(null);
  const barSvgRef = useRef(null);
  const [activeTab, setActiveTab] = useState('decay');
  const [selectedField, setSelectedField] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  // Set default selected field when data loads
  useEffect(() => {
    if (data && data.fields && data.fields.length > 0 && selectedField === null) {
      setSelectedField(data.fields[0].field);
    }
  }, [data, selectedField]);

  const currentField = data && data.fields
    ? data.fields.find((f) => f.field === selectedField) || data.fields[0]
    : null;

  // Decay curves chart
  useEffect(() => {
    if (activeTab !== 'decay') return;
    if (!currentField || !currentField.decayCurve) return;
    if (!decaySvgRef.current) return;

    const svg = d3.select(decaySvgRef.current);
    svg.selectAll('*').remove();

    const innerW = SVG_WIDTH - MARGIN.left - MARGIN.right;
    const innerH = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;

    const { decayCurve, actualDeviations, halfLife } = currentField;

    if (!decayCurve.length) return;

    const maxT = Math.max(
      d3.max(decayCurve, (d) => d.t) || 0,
      d3.max(actualDeviations || [], (d) => d.t) || 0
    );

    const xScale = d3.scaleLinear()
      .domain([0, maxT])
      .range([0, innerW]);

    const yScale = d3.scaleLinear()
      .domain([0, 1])
      .range([innerH, 0]);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    // Grid lines
    const yTicks = [0.25, 0.5, 0.75, 1.0];
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Horizontal dashed line at 0.5 (half-life marker)
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', yScale(0.5))
      .attr('y2', yScale(0.5))
      .attr('stroke', '#9ca3af')
      .attr('stroke-dasharray', '6,4')
      .attr('stroke-width', 1);

    // Label for 0.5 line
    g.append('text')
      .attr('x', innerW + 4)
      .attr('y', yScale(0.5))
      .attr('dominant-baseline', 'central')
      .attr('font-size', '9px')
      .attr('fill', '#9ca3af')
      .text('50%');

    // Vertical dashed line at the half-life period
    if (halfLife != null && halfLife <= maxT) {
      const hlX = xScale(halfLife);

      g.append('line')
        .attr('x1', hlX)
        .attr('x2', hlX)
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#6b7280')
        .attr('stroke-dasharray', '4,3')
        .attr('stroke-width', 1)
        .attr('opacity', 0.7);

      // Half-life label
      g.append('text')
        .attr('x', hlX)
        .attr('y', MARGIN.top - 8)
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', '#374151')
        .text(`t\u00BD = ${halfLife}`);
    }

    // Theoretical exponential decay curve (solid line)
    const lineGen = d3.line()
      .x((d) => xScale(d.t))
      .y((d) => yScale(d.value))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(decayCurve)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2);

    // Actual deviations (scatter dots)
    if (actualDeviations && actualDeviations.length) {
      actualDeviations.forEach((pt) => {
        g.append('circle')
          .attr('cx', xScale(pt.t))
          .attr('cy', yScale(pt.deviation))
          .attr('r', 4)
          .attr('fill', '#fff')
          .attr('stroke', '#f97316')
          .attr('stroke-width', 2);
      });
    }

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(xScale)
          .ticks(Math.min(Math.ceil(maxT), 10))
          .tickSize(0)
          .tickFormat((d) => d)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('dy', '0.75em');

    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 34)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Period');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(yScale)
          .ticks(5)
          .tickSize(0)
          .tickFormat((d) => `${Math.round(d * 100)}%`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -46)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Deviation from Mean');

    // Invisible overlay for tooltip
    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const tVal = xScale.invert(mx);

        // Find closest decay curve point
        let closestCurve = decayCurve[0];
        let closestDist = Math.abs(tVal - decayCurve[0].t);
        for (let i = 1; i < decayCurve.length; i++) {
          const dist = Math.abs(tVal - decayCurve[i].t);
          if (dist < closestDist) {
            closestDist = dist;
            closestCurve = decayCurve[i];
          }
        }

        // Find closest actual deviation point
        let closestActual = null;
        if (actualDeviations && actualDeviations.length) {
          let aDist = Infinity;
          for (const pt of actualDeviations) {
            const dist = Math.abs(tVal - pt.t);
            if (dist < aDist) {
              aDist = dist;
              closestActual = pt;
            }
          }
        }

        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          type: 'decay',
          period: closestCurve.t,
          theoretical: closestCurve.value,
          actual: closestActual ? closestActual.deviation : null,
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, currentField, activeTab]);

  // By State bar chart
  useEffect(() => {
    if (activeTab !== 'state') return;
    if (!data || !data.byState || data.byState.length === 0) return;
    if (!barSvgRef.current) return;

    const svg = d3.select(barSvgRef.current);
    svg.selectAll('*').remove();

    const sorted = [...data.byState].sort((a, b) => a.halfLife - b.halfLife);

    const barRowHeight = 28;
    const barMargin = { top: 20, right: 80, bottom: 30, left: 50 };
    const barChartHeight = barMargin.top + barMargin.bottom + sorted.length * barRowHeight;
    const innerW = SVG_WIDTH - barMargin.left - barMargin.right;
    const innerH = barChartHeight - barMargin.top - barMargin.bottom;

    svg
      .attr('viewBox', `0 0 ${SVG_WIDTH} ${barChartHeight}`)
      .attr('width', '100%')
      .attr('height', barChartHeight);

    const g = svg.append('g').attr('transform', `translate(${barMargin.left},${barMargin.top})`);

    const maxHL = d3.max(sorted, (d) => d.halfLife) || 10;

    const xScale = d3.scaleLinear()
      .domain([0, maxHL * 1.15])
      .range([0, innerW]);

    const yScale = d3.scaleBand()
      .domain(sorted.map((d) => d.state))
      .range([0, innerH])
      .padding(0.3);

    // Grid lines
    const xTicks = xScale.ticks(5);
    g.selectAll('.bar-grid')
      .data(xTicks)
      .enter()
      .append('line')
      .attr('x1', (d) => xScale(d))
      .attr('x2', (d) => xScale(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Bars
    sorted.forEach((d) => {
      const barColor = getBarColor(d.halfLife);

      g.append('rect')
        .attr('x', 0)
        .attr('y', yScale(d.state))
        .attr('width', xScale(d.halfLife))
        .attr('height', yScale.bandwidth())
        .attr('fill', barColor)
        .attr('rx', 3)
        .attr('opacity', 0.85);

      // Value label at end of bar
      g.append('text')
        .attr('x', xScale(d.halfLife) + 6)
        .attr('y', yScale(d.state) + yScale.bandwidth() / 2)
        .attr('dominant-baseline', 'central')
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', barColor)
        .text(`${d.halfLife.toFixed(1)} (${d.halfLifeDays.toFixed(0)}d)`);
    });

    // Y axis (state labels)
    g.append('g')
      .call(
        d3.axisLeft(yScale)
          .tickSize(0)
      )
      .call((axis) => axis.select('.domain').remove())
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151');

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(xScale)
          .ticks(5)
          .tickSize(0)
          .tickFormat((d) => d)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 24)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Half-Life (periods)');

    // Hover overlay per bar
    sorted.forEach((d) => {
      g.append('rect')
        .attr('x', 0)
        .attr('y', yScale(d.state))
        .attr('width', innerW)
        .attr('height', yScale.bandwidth())
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: 'state',
            state: d.state,
            halfLife: d.halfLife,
            halfLifeDays: d.halfLifeDays,
            addressCount: d.addressCount,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, activeTab]);

  // Empty state
  if (!data) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Half-Life Analysis</div>
        <div style={styles.subtitle}>Ornstein-Uhlenbeck mean-reversion speed estimation</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Half-Life Analysis</div>
      <div style={styles.subtitle}>Ornstein-Uhlenbeck mean-reversion speed estimation</div>

      {/* Tabs */}
      <div style={styles.tabRow}>
        <button
          style={activeTab === 'decay' ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab('decay')}
        >
          Decay Curves
        </button>
        <button
          style={activeTab === 'state' ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab('state')}
        >
          By State
        </button>
      </div>

      {/* Decay Curves View */}
      {activeTab === 'decay' && (
        <>
          {/* Field selector */}
          {data.fields && data.fields.length > 0 && (
            <div style={styles.controlsRow}>
              <span style={styles.selectLabel}>Field:</span>
              <select
                style={styles.select}
                value={selectedField || ''}
                onChange={(e) => setSelectedField(e.target.value)}
              >
                {data.fields.map((f) => (
                  <option key={f.field} value={f.field}>{f.field}</option>
                ))}
              </select>
            </div>
          )}

          {/* Info cards for OU parameters */}
          {currentField && (
            <div style={styles.infoCardsRow}>
              <div style={styles.infoCard}>
                <div style={styles.infoValue}>
                  {currentField.halfLife != null ? currentField.halfLife.toFixed(1) : '--'}
                </div>
                <div style={styles.infoLabel}>Half-Life</div>
                <div style={styles.infoSublabel}>
                  {currentField.halfLifeDays != null
                    ? `~${currentField.halfLifeDays.toFixed(1)} days`
                    : 'periods'}
                </div>
              </div>
              <div style={styles.infoCard}>
                <div style={styles.infoValue}>
                  {currentField.theta != null ? currentField.theta.toFixed(3) : '--'}
                </div>
                <div style={styles.infoLabel}>&theta; (theta)</div>
                <div style={styles.infoSublabel}>Speed of reversion</div>
              </div>
              <div style={styles.infoCard}>
                <div style={styles.infoValue}>
                  {currentField.mu != null ? `$${currentField.mu.toFixed(2)}` : '--'}
                </div>
                <div style={styles.infoLabel}>&mu; (mu)</div>
                <div style={styles.infoSublabel}>Long-run mean</div>
              </div>
              <div style={styles.infoCard}>
                <div style={styles.infoValue}>
                  {currentField.sigma != null ? currentField.sigma.toFixed(3) : '--'}
                </div>
                <div style={styles.infoLabel}>&sigma; (sigma)</div>
                <div style={styles.infoSublabel}>Volatility</div>
              </div>
              <div style={styles.infoCard}>
                <div style={styles.infoValue}>
                  {currentField.rSquared != null ? currentField.rSquared.toFixed(2) : '--'}
                </div>
                <div style={styles.infoLabel}>R&sup2;</div>
                <div style={styles.infoSublabel}>Fit quality</div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div style={styles.legend}>
            <div style={styles.legendItem}>
              <svg width="20" height="10">
                <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2" />
              </svg>
              <span>Theoretical Decay</span>
            </div>
            <div style={styles.legendItem}>
              <svg width="16" height="16">
                <circle cx="8" cy="8" r="4" fill="#fff" stroke="#f97316" strokeWidth="2" />
              </svg>
              <span>Actual Deviations</span>
            </div>
            <div style={styles.legendItem}>
              <svg width="20" height="10">
                <line x1="0" y1="5" x2="20" y2="5" stroke="#9ca3af" strokeWidth="1" strokeDasharray="6,4" />
              </svg>
              <span>Half-Life (50%)</span>
            </div>
          </div>

          {/* Decay curve chart */}
          <div style={styles.container}>
            <svg
              ref={decaySvgRef}
              viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
              width="100%"
              preserveAspectRatio="xMidYMid meet"
              style={{ display: 'block' }}
            />
          </div>
        </>
      )}

      {/* By State View */}
      {activeTab === 'state' && (
        <>
          {/* Color legend for bar chart */}
          <div style={{ ...styles.legend, marginBottom: '12px' }}>
            <div style={styles.legendItem}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: '#16a34a', display: 'inline-block' }} />
              <span>Short (&le;3)</span>
            </div>
            <div style={styles.legendItem}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: '#f59e0b', display: 'inline-block' }} />
              <span>Medium (3-6)</span>
            </div>
            <div style={styles.legendItem}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: '#dc2626', display: 'inline-block' }} />
              <span>Long (&gt;6)</span>
            </div>
          </div>

          <div style={styles.container}>
            <svg
              ref={barSvgRef}
              viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
              width="100%"
              preserveAspectRatio="xMidYMid meet"
              style={{ display: 'block' }}
            />
          </div>
        </>
      )}

      {/* Tooltip */}
      {tooltip && tooltip.type === 'decay' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            Period {tooltip.period}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#2563eb' }}>Theoretical</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {(tooltip.theoretical * 100).toFixed(1)}%
            </span>
          </div>
          {tooltip.actual != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#f97316' }}>Actual</span>
              <span style={{ fontWeight: 600, color: '#1f2937' }}>
                {(tooltip.actual * 100).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      )}
      {tooltip && tooltip.type === 'state' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Half-life</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.halfLife.toFixed(1)} periods
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Days</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              ~{tooltip.halfLifeDays.toFixed(1)}d
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>Addresses</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.addressCount}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
